// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useEffect, useRef, useState } from "react";
import {
  Tag,
  Settings,
  Link,
  Clock,
  RefreshCw,
  FileText,
  Code,
  Save,
  X,
  Trash2,
  AlertTriangle,
  Server,
  Brain,
  CheckCircle,
  XCircle,
  DollarSign,
  Clock as ClockIcon,
  AlertTriangle as AlertTriangleIcon,
} from "lucide-react";

const SYSTEM_ENDPOINTS = [
  {
    id: "llm_evaluate_booking",
    name: "LLM Booking Evaluation",
    endpoint: "/api/v2/llm/evaluate-booking/{{uuid}}",
    method: "POST",
    description: "Run LLM evaluation for booking analysis",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "{{api_token}}",
    },
    payload_template: {
      uuid: "{{booking_uuid}}",
      evaluation_type: "booking_analysis",
      timestamp: "{{timestamp}}",
    },
    trigger_status: "submitted",
  },
  {
    id: "booking_status_update",
    name: "Booking Status Update",
    endpoint: "/api/v2/bookings/{{uuid}}/status",
    method: "PUT",
    description: "Update booking status in the system",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "{{api_token}}",
    },
    payload_template: {
      status: "{{status_code}}",
      updated_at: "{{timestamp}}",
      updated_by: "{{user_id}}",
    },
    requires_status_code: true,
    status_code_options: [
      "draft",
      "submitted",
      "waiting",
      "processed",
      "confirmed",
      "paid",
      "payment-error",
      "payment-pending",
      "canceled",
      "on-hold",
      "postponed",
    ],
  },
];

const BOOKING_STATUSES = [
  { value: "none", label: "None", icon: <XCircle className="w-4 h-4" /> },
  { value: "draft", label: "Draft", icon: <FileText className="w-4 h-4" /> },
  {
    value: "submitted",
    label: "Submitted",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  {
    value: "waiting",
    label: "Waiting",
    icon: <ClockIcon className="w-4 h-4" />,
  },
  {
    value: "processed",
    label: "Processed",
    icon: <Settings className="w-4 h-4" />,
  },
  {
    value: "confirmed",
    label: "Confirmed",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  { value: "paid", label: "Paid", icon: <DollarSign className="w-4 h-4" /> },
  {
    value: "payment-error",
    label: "Payment Error",
    icon: <AlertTriangleIcon className="w-4 h-4" />,
  },
  {
    value: "payment-pending",
    label: "Payment Pending",
    icon: <ClockIcon className="w-4 h-4" />,
  },
  {
    value: "canceled",
    label: "Canceled",
    icon: <XCircle className="w-4 h-4" />,
  },
  {
    value: "on-hold",
    label: "On Hold",
    icon: <AlertTriangleIcon className="w-4 h-4" />,
  },
  {
    value: "postponed",
    label: "Postponed",
    icon: <ClockIcon className="w-4 h-4" />,
  },
];

const ApiConfigurationForm = ({
  api: initialApi,
  isNew = false,
  onSave,
  onCancel,
  onDelete,
  currentStatus = null,
  canManage = false,
}) => {
  const [api, setApi] = useState(initialApi);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    headers: null,
    payload_template: null,
    call_name: null,
    endpoint_url: null,
    status_code: null,
  });
  const [selectedPredefinedEndpoint, setSelectedPredefinedEndpoint] =
    useState("");
  const [selectedStatusCode, setSelectedStatusCode] = useState("");

  const isInitialMount = useRef(true);
  const initializedRef = useRef(false);
  const statusCodeSelectRef = useRef(null);

  // Helper to determine if a status value is an actual chosen status
  // and not a template placeholder like "{{status_code}}"
  const isRealStatusCode = (value) => {
    if (!value || typeof value !== "string") return false;
    if (value.includes("{{") && value.includes("}}")) return false;
    return BOOKING_STATUSES.some((s) => s.value === value);
  };

  useEffect(() => {
    if (!initializedRef.current) {
      setApi(initialApi);
      initializedRef.current = true;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, []);

  useEffect(() => {
    if (api.endpoint_url && !selectedPredefinedEndpoint) {
      const matchingEndpoint = SYSTEM_ENDPOINTS.find(
        (ep) =>
          ep.endpoint === api.endpoint_url ||
          api.endpoint_url.includes(
            ep.endpoint.replace("{{booking_id}}", "").replace("{{uuid}}", ""),
          ),
      );

      if (matchingEndpoint) {
        setSelectedPredefinedEndpoint(matchingEndpoint.id);
      }
    }
  }, [api.endpoint_url, selectedPredefinedEndpoint]);

  useEffect(() => {
    if (api.payload_template && typeof api.payload_template === "object") {
      if (api.payload_template.status && !selectedStatusCode) {
        if (isRealStatusCode(api.payload_template.status)) {
          setSelectedStatusCode(api.payload_template.status);
        }
      } else if (api.payload_template.payment_status && !selectedStatusCode) {
        if (isRealStatusCode(api.payload_template.payment_status)) {
          setSelectedStatusCode(api.payload_template.payment_status);
        }
      }
    }
  }, [api.payload_template, selectedStatusCode]);

  const handleChange = (field, value) => {
    const updatedApi = { ...api, [field]: value };
    setApi(updatedApi);

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }

    // Validate the field after change
    setTimeout(() => validateField(field), 0);
  };

  const handlePredefinedEndpointChange = (endpointId) => {
    setSelectedPredefinedEndpoint(endpointId);

    if (endpointId) {
      const endpoint = SYSTEM_ENDPOINTS.find((ep) => ep.id === endpointId);

      if (endpoint) {
        setApi({
          ...api,
          call_name: endpoint.name,
          endpoint_url: endpoint.endpoint,
          http_method: endpoint.method,
          headers: endpoint.headers,
          payload_template: endpoint.payload_template,
        });

        // If this endpoint requires a status code, reset the selection
        if (
          endpoint.id === "booking_status_update" ||
          endpoint.id === "booking_approval" ||
          endpoint.id === "booking_rejection" ||
          endpoint.id === "payment_status_update" ||
          endpoint.requires_status_code
        ) {
          setSelectedStatusCode("");
          // Focus the status code field so the user sees what's required
          setTimeout(() => {
            if (statusCodeSelectRef.current) {
              statusCodeSelectRef.current.focus();
            }
          }, 0);
        }
      }
    }
  };

  const handleStatusCodeChange = (statusCode) => {
    setSelectedStatusCode(statusCode);
    if (validationErrors.status_code) {
      setValidationErrors((prev) => ({
        ...prev,
        status_code: null,
      }));
    }

    if (api.payload_template && typeof api.payload_template === "object") {
      const updatedPayload = { ...api.payload_template };
      if (Object.prototype.hasOwnProperty.call(updatedPayload, "status")) {
        updatedPayload.status = statusCode;
      }
      if (
        Object.prototype.hasOwnProperty.call(updatedPayload, "payment_status")
      ) {
        updatedPayload.payment_status = statusCode;
      }
      handleChange("payload_template", updatedPayload);
    }

    // Validate status code field after change
    setTimeout(() => validateField("status_code"), 0);
  };

  const getAvailableEndpoints = () => {
    if (currentStatus) {
      return SYSTEM_ENDPOINTS.filter(
        (ep) =>
          !ep.trigger_status || ep.trigger_status === currentStatus.status_name,
      );
    }
    return SYSTEM_ENDPOINTS;
  };

  const getStatusSpecificEndpoints = () => {
    if (!currentStatus) return [];
    return SYSTEM_ENDPOINTS.filter(
      (ep) => ep.trigger_status === currentStatus.status_name,
    );
  };

  const getSelectedEndpoint = () => {
    return SYSTEM_ENDPOINTS.find((ep) => ep.id === selectedPredefinedEndpoint);
  };

  const validateForm = () => {
    const errors = {};

    if (!api.call_name || api.call_name.trim() === "") {
      errors.call_name = "API Name ist erforderlich";
    }

    if (!api.endpoint_url || api.endpoint_url.trim() === "") {
      errors.endpoint_url = "Endpoint URL ist erforderlich";
    }
    if (
      !api.headers ||
      (typeof api.headers === "string" && api.headers.trim() === "")
    ) {
      errors.headers = "HTTP Headers sind erforderlich";
    } else if (typeof api.headers === "string") {
      try {
        const parsed = JSON.parse(api.headers);
        if (
          parsed &&
          typeof parsed === "object" &&
          Object.keys(parsed).length === 0
        ) {
          errors.headers = "HTTP Headers dürfen nicht leer sein";
        }
      } catch (e) {
        errors.headers = `Ungültiges JSON Format: ${e.message}`;
      }
    } else if (
      typeof api.headers === "object" &&
      Object.keys(api.headers).length === 0
    ) {
      errors.headers = "HTTP Headers dürfen nicht leer sein";
    }
    if (
      api.payload_template &&
      typeof api.payload_template === "string" &&
      api.payload_template.trim() !== ""
    ) {
      try {
        JSON.parse(api.payload_template);
      } catch (e) {
        errors.payload_template = `Ungültiges JSON Format: ${e.message}`;
      }
    }
    const endpoint = selectedPredefinedEndpoint ? getSelectedEndpoint() : null;
    const isStatusRelatedEndpoint = endpoint
      ? endpoint.id === "booking_status_update" ||
        endpoint.id === "booking_approval" ||
        endpoint.id === "booking_rejection" ||
        endpoint.id === "payment_status_update"
      : api.endpoint_url?.includes("/status") ||
        api.endpoint_url?.includes("/approve") ||
        api.endpoint_url?.includes("/reject") ||
        api.endpoint_url?.includes("/payment");

    if (
      isStatusRelatedEndpoint &&
      (!selectedStatusCode || selectedStatusCode.trim() === "")
    ) {
      errors.status_code = "Ziel Status Code ist erforderlich";
    }

    // Overwrite validation errors with the newly computed ones
    setValidationErrors({
      headers: null,
      payload_template: null,
      call_name: null,
      endpoint_url: null,
      status_code: null,
      ...errors,
    });

    if (errors.status_code && statusCodeSelectRef.current) {
      statusCodeSelectRef.current.focus();
    }

    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    const apiToSave = { ...api };

    if (
      typeof apiToSave.headers === "string" &&
      apiToSave.headers.trim() !== ""
    ) {
      try {
        apiToSave.headers = JSON.parse(apiToSave.headers);
      } catch (error) {
        console.error("Error parsing headers JSON:", error);
        return;
      }
    } else if (!apiToSave.headers || apiToSave.headers === "") {
      apiToSave.headers = null;
    }
    if (
      typeof apiToSave.payload_template === "string" &&
      apiToSave.payload_template.trim() !== ""
    ) {
      try {
        apiToSave.payload_template = JSON.parse(apiToSave.payload_template);
      } catch (error) {
        console.error("Error parsing payload_template JSON:", error);
        return;
      }
    } else if (
      !apiToSave.payload_template ||
      apiToSave.payload_template === ""
    ) {
      apiToSave.payload_template = {};
    }

    setIsSaving(true);
    try {
      await onSave(apiToSave);
    } catch (error) {
      console.error("Error saving API:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete(api.api_call_id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting API:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const getTitle = () => {
    if (isNew) {
      return "Neue System API hinzufügen";
    }
    return api.call_name;
  };

  const getDescription = () => {
    if (isNew) {
      return "Konfigurieren Sie eine neue System API-Integration";
    }
    return "System API für Workflow-Automatisierung";
  };

  const handleJSONChange = (field, value) => {
    handleChange(field, value);
  };

  const validateField = (fieldName) => {
    const errors = {};

    if (
      fieldName === "call_name" &&
      (!api.call_name || api.call_name.trim() === "")
    ) {
      errors.call_name = "API Name ist erforderlich";
    }

    if (
      fieldName === "endpoint_url" &&
      (!api.endpoint_url || api.endpoint_url.trim() === "")
    ) {
      errors.endpoint_url = "Endpoint URL ist erforderlich";
    }

    if (fieldName === "status_code") {
      const isStatusRelatedEndpoint = selectedPredefinedEndpoint
        ? (() => {
            const endpoint = getSelectedEndpoint();
            return (
              endpoint &&
              (endpoint.id === "booking_status_update" ||
                endpoint.id === "booking_approval" ||
                endpoint.id === "booking_rejection" ||
                endpoint.id === "payment_status_update")
            );
          })()
        : api.endpoint_url?.includes("/status") ||
          api.endpoint_url?.includes("/approve") ||
          api.endpoint_url?.includes("/reject") ||
          api.endpoint_url?.includes("/payment");

      if (
        isStatusRelatedEndpoint &&
        (!selectedStatusCode || selectedStatusCode.trim() === "")
      ) {
        errors.status_code = "Ziel Status Code ist erforderlich";
      }
    }

    if (fieldName === "headers") {
      if (
        !api.headers ||
        (typeof api.headers === "string" && api.headers.trim() === "")
      ) {
        errors.headers = "HTTP Headers sind erforderlich";
      } else if (
        api.headers &&
        typeof api.headers === "string" &&
        api.headers.trim() !== ""
      ) {
        try {
          const parsedHeaders = JSON.parse(api.headers);
          // Check if it's an empty object
          if (
            typeof parsedHeaders === "object" &&
            Object.keys(parsedHeaders).length === 0
          ) {
            errors.headers = "HTTP Headers dürfen nicht leer sein";
          }
        } catch (error) {
          errors.headers = `Ungültiges JSON Format: ${error.message}`;
        }
      }
    }

    if (
      fieldName === "payload_template" &&
      api.payload_template &&
      typeof api.payload_template === "string" &&
      api.payload_template.trim() !== ""
    ) {
      try {
        JSON.parse(api.payload_template);
      } catch (error) {
        errors.payload_template = `Ungültiges JSON Format: ${error.message}`;
      }
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));
  };

  return (
    <>
      <div
        className={`bg-white dark:bg-slate-800 border rounded-lg md:p-6 p-4 shadow-sm dark:border-slate-600 ${isNew ? "border-blue-200 dark:border-blue-600 border-2" : "border-slate-200 dark:border-slate-600"}`}
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-start mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center items-start gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Server className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {getTitle()}
              </h2>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {getDescription()}
              </p>
              {currentStatus && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Workflow Status: {currentStatus.status_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={api.is_enabled || false}
                onChange={(e) => handleChange("is_enabled", e.target.checked)}
                className="rounded border-gray-300 dark:border-slate-500 text-blue-600 focus:ring-blue-500 dark:bg-slate-700"
              />
              <span className="text-sm text-gray-700 dark:text-slate-300">
                Aktiv
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={api.is_blocking || false}
                onChange={(e) => handleChange("is_blocking", e.target.checked)}
                className="rounded border-gray-300 dark:border-slate-500 text-red-600 focus:ring-red-500 dark:bg-slate-700"
              />
              <span className="text-sm text-gray-700 dark:text-slate-300">
                Blockierend
              </span>
            </label>
          </div>
        </div>

        {/* Status-specific recommendations */}
        {currentStatus && currentStatus.status_name === "submitted" && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Empfohlene APIs für &ldquo;Submitted&rdquo; Status
              </h3>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              Für den &ldquo;Submitted&rdquo; Status wird empfohlen, die
              LLM-Evaluierung zu konfigurieren:
            </p>
            <div className="space-y-2">
              {getStatusSpecificEndpoints().map((endpoint) => (
                <div
                  key={endpoint.id}
                  className="flex items-center justify-between p-2 bg-white dark:bg-slate-700/50 rounded border dark:border-slate-600"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      {endpoint.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-400">
                      {endpoint.description}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePredefinedEndpointChange(endpoint.id)}
                    className="px-3 py-1 text-xs bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
                  >
                    Verwenden
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Predefined System Endpoints */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Server className="w-4 h-4 inline mr-1" />
            Vordefinierte System APIs
          </label>
          <select
            value={selectedPredefinedEndpoint}
            onChange={(e) => handlePredefinedEndpointChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
          >
            <option value="">
              Wählen Sie eine vordefinierte System API...
            </option>
            {getAvailableEndpoints().map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} - {endpoint.description}
                {endpoint.trigger_status && ` (${endpoint.trigger_status})`}
              </option>
            ))}
          </select>
          {selectedPredefinedEndpoint && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg dark:border dark:border-slate-600">
              <p className="text-sm text-gray-600 dark:text-slate-400">
                <strong>Endpoint:</strong> {api.endpoint_url}
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                <strong>Methode:</strong> {api.http_method}
              </p>
            </div>
          )}
        </div>

        {/* Status Code Configuration - Only show for status-related endpoints */}
        {(() => {
          // Check if current endpoint is status-related
          const isStatusRelatedEndpoint = selectedPredefinedEndpoint
            ? (() => {
                const endpoint = getSelectedEndpoint();
                return (
                  endpoint &&
                  (endpoint.id === "booking_status_update" ||
                    endpoint.id === "booking_approval" ||
                    endpoint.id === "booking_rejection" ||
                    endpoint.id === "payment_status_update")
                );
              })()
            : // For custom endpoints, check URL patterns
              api.endpoint_url?.includes("/status") ||
              api.endpoint_url?.includes("/approve") ||
              api.endpoint_url?.includes("/reject") ||
              api.endpoint_url?.includes("/payment");

          return isStatusRelatedEndpoint ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Ziel Status Code{" "}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <select
                ref={statusCodeSelectRef}
                value={selectedStatusCode}
                onChange={(e) => handleStatusCodeChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors ${
                  validationErrors.status_code
                    ? "border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-200 dark:border-slate-600"
                }`}
              >
                <option value="">Wählen Sie den Ziel-Status...</option>
                {BOOKING_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {validationErrors.status_code && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {validationErrors.status_code}
                </p>
              )}
              {selectedStatusCode && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-sm">
                  <p className="text-green-800 dark:text-green-200">
                    <strong>Beispiel:</strong> Wenn eine Buchung zu &ldquo;
                    {selectedStatusCode}&rdquo; geändert wird, wird der
                    API-Aufruf mit{" "}
                    <code className="bg-green-100 dark:bg-green-900/40 px-1 rounded">
                      &quot;status&quot;: &quot;{selectedStatusCode}&quot;
                    </code>{" "}
                    ausgeführt.
                  </p>
                </div>
              )}
            </div>
          ) : null;
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              Name <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              type="text"
              value={api?.call_name || ""}
              onChange={(e) => handleChange("call_name", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors ${
                validationErrors.call_name
                  ? "border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-200 dark:border-slate-600"
              }`}
              placeholder="API-Aufruf Name"
            />
            {validationErrors.call_name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {validationErrors.call_name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <Settings className="w-4 h-4 inline mr-1" />
              HTTP Methode
            </label>
            <select
              value={api.http_method || "POST"}
              onChange={(e) => handleChange("http_method", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Link className="w-4 h-4 inline mr-1" />
            Endpoint URL{" "}
            <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="text"
            value={api.endpoint_url || ""}
            onChange={(e) => handleChange("endpoint_url", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors ${
              validationErrors.endpoint_url
                ? "border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-200 dark:border-slate-600"
            }`}
            placeholder="/api/v2/llm/evaluate-booking/{{uuid}}"
          />
          {validationErrors.endpoint_url && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {validationErrors.endpoint_url}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Timeout (Sekunden)
            </label>
            <input
              type="number"
              value={api.timeout_seconds || 30}
              onChange={(e) =>
                handleChange("timeout_seconds", parseInt(e.target.value) || 30)
              }
              min="1"
              max="300"
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Wiederholungen
            </label>
            <input
              type="number"
              value={api.retry_attempts || 3}
              onChange={(e) =>
                handleChange("retry_attempts", parseInt(e.target.value) || 0)
              }
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            HTTP Headers (JSON){" "}
            <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <textarea
            rows="4"
            value={
              typeof api.headers === "string"
                ? api.headers
                : JSON.stringify(api.headers || null, null, 2)
            }
            onChange={(e) => handleJSONChange("headers", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 ${
              validationErrors.headers
                ? "border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-200 dark:border-slate-600"
            }`}
            placeholder='{"Content-Type": "application/json", "x-api-key": "{{api_token}}"}'
          />
          {validationErrors.headers && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {validationErrors.headers}
            </p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Code className="w-4 h-4 inline mr-1" />
            Request Body Template (JSON)
          </label>
          <textarea
            rows="4"
            value={
              typeof api.payload_template === "string"
                ? api.payload_template
                : JSON.stringify(api.payload_template || {}, null, 2)
            }
            onChange={(e) =>
              handleJSONChange("payload_template", e.target.value)
            }
            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-200 ${
              validationErrors.payload_template
                ? "border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-200 dark:border-slate-600"
            }`}
            placeholder='{"booking_uuid": "{{booking_uuid}}", "evaluation_type": "booking_analysis"}'
          />
          {validationErrors.payload_template && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {validationErrors.payload_template}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 md:mt-6 mt-4">
          {canManage && !isNew && (
            <button
              onClick={handleDeleteClick}
              disabled={isNew || isSaving}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-2 inline" />
              Entfernen
            </button>
          )}
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4 mr-2 inline" />
            {isNew ? "Abbrechen" : "Abbrechen"}
          </button>
          {canManage && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 border border-blue-600 dark:border-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center sm:justify-start justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isNew ? "Hinzufügen..." : "Speichern..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isNew ? "Hinzufügen" : "Speichern"}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6 dark:border dark:border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  API löschen
                </h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Sind Sie sicher, dass Sie diese API-Konfiguration löschen
                  möchten?
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4 dark:border dark:border-slate-600">
              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                {api.call_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {api.endpoint_url}
              </p>
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 border border-red-600 dark:border-red-500 rounded-md hover:bg-red-700 dark:hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Löschen...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApiConfigurationForm;
